// src/services/api.ts
 import { useOfflineStorage } from '../hooks/useOfflineStorage'

import axios, { AxiosInstance, AxiosResponse,AxiosError } from 'axios';
import { API_URL, TOKEN_KEY,API_URL_JUEGO } from '../config/constants';
import { showAlert } from "../utils/alert";
 
// Interfaz base para datos de respuesta
interface BaseResponseData {
  msg?: string;
  [key: string]: any;
}

// Interfaz genérica para la respuesta de la API que extiende BaseResponseData
interface ApiResponse<T extends BaseResponseData = BaseResponseData> {
  response: string;
  data: T;
}


interface ApiResponseData {
  msg?: string;
  token?: string;
  [key: string]: any;
}

// Función para obtener los datos del usuario del localStorage
const getUserData = () => {
  const userStr = localStorage.getItem('user');
  if (!userStr) return null;
  
  try {
    const user = JSON.parse(userStr);
    return {
      localId: user.localId,
      userId: user.dataUser.id,
      dataUser: user.dataUser,
      cashregisterId: user.cashregisterId
    };
  } catch (error) {
    console.error('Error parsing user data:', error);
    return null;
  }
};

export const api: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Instancia para juegos
export const gameApi = axios.create({
  baseURL: API_URL_JUEGO,
  headers: {
    'Content-Type': 'application/json',
  },
});

axios.interceptors.response.use(
  response => response,
  error => {
    if (!navigator.onLine) {
      // Personaliza el mensaje de error para cuando estés offline
      return Promise.reject(new Error('No hay conexión a internet'));
    }
    return Promise.reject(error);
  }
);

// Configurar interceptores para ambas instancias
[api, gameApi].forEach(instance => {
  // Interceptor de peticiones
  instance.interceptors.request.use((config) => {
    // Obtener el token
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      config.headers['Token'] = `Bearer ${token}`;
    }

    // Obtener datos del usuario
    const userData = getUserData();
    
    // Si hay datos del usuario y el método no es GET, añadirlos al body
    if (userData && config.method !== 'get') {
      const currentData = config.data || {};
      
      // Si la data actual es FormData, añadimos los campos
      if (currentData instanceof FormData) {
        if (!currentData.has('localId')) currentData.append('localId', userData.localId);
        if (!currentData.has('userId')) currentData.append('userId', userData.userId);
        if (!currentData.has('cashregisterId')) currentData.append('cashregisterId', userData.cashregisterId);
      } else {
        // Si es un objeto JSON, mezclamos los datos
        config.data = {
          ...currentData,
          localId: currentData.localId || userData.localId,
          userId: currentData.userId || userData.userId,
          cashregisterId: currentData.cashregisterId || userData.cashregisterId
        };
      }
    }

    // Si es GET y hay datos de usuario, añadirlos como query params
    if (userData && config.method === 'get') {
      config.params = {
        ...config.params,
        localId: userData.localId,
        userId: userData.userId,
        cashregisterId: userData.cashregisterId
      };
    }

    return config;
  }, (error) => {
    return Promise.reject(error);
  });
});

// Función auxiliar para manejar las respuestas de la API
export const handleApiResponse = <T extends BaseResponseData>(
  response: AxiosResponse<ApiResponse<T>>
): ApiResponse<T> => {
  if (response.data.response === "0") {
    const errorMessage = response.data.data.msg || 'Error en la operación';
    
    if (errorMessage.toLowerCase().includes('acceso denegado')) {
      handleRedirectToLogin();
    } else {
      showAlert(errorMessage,'error');
    }
    
    throw new Error(errorMessage);
  }
  
  return response.data;
};
 
// Función para manejar errores
// const handleApiError = (error: AxiosError): never => {
//   if (error.response) {
//     // El servidor respondió con un código de error
//     throw new Error(error.response.data?.msg || 'Error en el servidor');
//   } else if (error.request) {
//     // La petición se hizo pero no se recibió respuesta
//     throw new Error('No se recibió respuesta del servidor');
//   } else {
//     // Error en la configuración de la petición
//     throw new Error('Error al realizar la petición');
//   }
// };



// src/utils/api.ts

export const createOfflineCapableRequest = async (
  endpoint: string,
  method: string,
  data?: any
) => {
  try {
    const response = await api.request({
      url: endpoint,
      method,
      data
    })
    return response.data
  } catch (error) {
    if (!navigator.onLine) {
      // Guardar la petición para sincronizar cuando haya conexión
      const offlineRequests = JSON.parse(
        localStorage.getItem('offlineRequests') || '[]'
      )
      offlineRequests.push({
        endpoint,
        method,
        data,
        timestamp: Date.now()
      })
      localStorage.setItem('offlineRequests', JSON.stringify(offlineRequests))
      throw new Error('No hay conexión a internet. La operación se guardó para sincronización posterior.')
    }
    throw error
  }
}

const handleRedirectToLogin = () => { 
  showAlert('Redireccionando al login...','info'); 
  setTimeout(() => {
    window.location.href = '/login';
  }, 2000); // Espera 2 segundos antes de redireccionar
};

// Función auxiliar para manejar errores
export const handleApiError = (error: any): never => {
  if (axios.isAxiosError(error) && error.response) {
    const responseData = error.response.data as ApiResponse;
    const errorMessage = error.response.data?.data?.msg || 'Error en la operación';
      if (errorMessage.toLowerCase().includes('acceso denegado')) {
      handleRedirectToLogin();
    } else {
      showAlert( 'error',errorMessage);
    }

    throw new Error(responseData.data.msg || 'Error en la operación');
  }
  throw error;
};


export const getInitialToken = async (): Promise<string> => {
  try {
    const response = await api.post<ApiResponse<{ token: string }>>('/initSessionServer');
    return handleApiResponse(response).data.token;
  } catch (error) {
    console.error('Error obteniendo el token inicial:', error);
    throw handleApiError(error);
  }
};

export const loginUser = async (username: string, password: string, token: string): Promise<any> => {
  try {
    const response = await api.post<ApiResponse>('/loginUser',
      { userName: username, password },
      { headers: { 'Token': `Bearer ${token}` } }
    );
    return handleApiResponse(response);
  } catch (error) {
    throw handleApiError(error);
  }
};

export const requestResetCode = async (
  username: string, 
  token: string
): Promise<ApiResponse> => {
  try {
    const response = await api.post<ApiResponse>(
      '/requestResetCode',
      { username },
      {
        headers: {
          'Token': `Bearer ${token}`
        }
      }
    );
    return handleApiResponse(response);
  } catch (error) {
    throw handleApiError(error);
  }
};

export const verifyAndResetPassword  = async (
  username: string,
  code: string,
  newPassword: string,
  confirmPassword: string,
  token: string
): Promise<ApiResponse> => {
  try {
    const response = await api.post<ApiResponse>(
      '/verifyAndResetPassword',
      {
        username,
        codigo: code,
        newPassword,
        confirmPassword
      },
      {
        headers: {
          'Token': `Bearer ${token}`
        }
      }
    );
    return handleApiResponse(response);
  } catch (error) {
    throw handleApiError(error);
  }
};


export const setAuthToken = (token: string): void => {
  api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
};

export const fetchUserData = async (): Promise<any> => {
  try {
    const response = await api.get<ApiResponse>('/user');
    return handleApiResponse(response);
  } catch (error) {
    throw handleApiError(error);
  }
};

// Función para obtener datos de la caja
export const fetchCajaData = async (userId: string): Promise<any> => {
  try {
    const response = await api.post<ApiResponse>('/dataCashier', { idUser: userId });
    return handleApiResponse(response);
  } catch (error) {
    throw handleApiError(error);
  }
};

// Función para verificar si la caja está abierta
export const checkCajaStatus = async (userId: string): Promise<{ isOpen: boolean; data?: any }> => {
  try {
    const response = await api.post<ApiResponse>('/dataCashier', { idUser: userId });
    const result = handleApiResponse(response);
    
    console.log('🔍 checkCajaStatus - Datos recibidos:', result);
    console.log('🔍 checkCajaStatus - cashregister value:', result.data?.cashregister);
    console.log('🔍 checkCajaStatus - cashregister type:', typeof result.data?.cashregister);
    
    // Verificar si la caja está abierta
    // Si cashregister es false, la caja está cerrada
    // Si cashregister es un objeto, la caja está abierta
        
    
    let isOpen = result.data.cashregister === false ? false : true;
   
    if( result.response !== 'success' && !result.data){
      isOpen = false;
    }
    
     
    
    console.log('🔍 checkCajaStatus - isOpen:', isOpen);
    
    return {
      isOpen,
      data: result.data
    };
  } catch (error) {
    console.error('Error verificando estado de caja:', error);
    return {
      isOpen: false,
      data: null
    };
  }
};

// Función para abrir la caja
export const openCaja = async (userId: string, openingData: any): Promise<any> => {
  try {
    const response = await api.post<ApiResponse>('/cashRegisterOpening', {
      idUser: userId,
      ...openingData
    });
    return handleApiResponse(response);
  } catch (error) {
    throw handleApiError(error);
  }
};

// Función para cerrar la caja
export const closeCaja = async (userId: string, closingData: any): Promise<any> => {
  try {
    const response = await api.post<ApiResponse>('/cashRegisterClosing', {
      idUser: userId,
      ...closingData
    });
    return handleApiResponse(response);
  } catch (error) {
    throw handleApiError(error);
  }
};

// Función para ingreso de dinero a la caja
export const cashEntry = async (userId: string, entryData: any): Promise<any> => {
  try {
    const response = await api.post<ApiResponse>('/entryCashRegister', {
      idUser: userId,
      ...entryData
    });
    return handleApiResponse(response);
  } catch (error) {
    throw handleApiError(error);
  }
};

export const egressService = async (userId: string, egressData: any): Promise<any> => {
  try {
    const response = await api.post<ApiResponse>('/egressServiceCashRegister', {
      idUser: userId,
      ...egressData
    });
    return handleApiResponse(response);
  } catch (error) {
    throw handleApiError(error);
  }
};

// Función para corte de dinero de la caja
export const cashCut = async (userId: string, cutData: any): Promise<any> => {
  try {
    const response = await api.post<ApiResponse>('/egressCashRegister', {
      idUser: userId,
      ...cutData
    });
    return handleApiResponse(response);
  } catch (error) {
    throw handleApiError(error);
  }
};

// Función para ingreso de fichas
export const coinEntry = async (userId: string, coinEntryData: any): Promise<any> => {
  try {
    const response = await api.post<ApiResponse>('/entryCoinsCashRegister', {
      idUser: userId,
      ...coinEntryData
    });
    return handleApiResponse(response);
  } catch (error) {
    throw handleApiError(error);
  }
};
 
// Función para pago manual
export const manualPayment = async (paymentData: any): Promise<any> => {
  try {
    const response = await api.post<ApiResponse>('/manualPaymentCashRegister', paymentData);
    return handleApiResponse(response);
  } catch (error) {
    throw handleApiError(error);
  }
};

// Función para corte de fichas
export const coinCut = async (userId: string, coinCutData: any): Promise<any> => {
  try {
    const response = await api.post<ApiResponse>('/egressCoinsCashRegister', {
      idUser: userId,
      ...coinCutData
    });
    return handleApiResponse(response);
  } catch (error) {
    throw handleApiError(error);
  }
};

// Función para ver movimientos
export const fetchMovements = async (userId: string, cajaId: string): Promise<any> => {
  try {
    const response = await api.post<ApiResponse>('/movements', {
      idUser: userId,
      cashregister_id: cajaId
    });
    return handleApiResponse(response);
  } catch (error) {
    throw handleApiError(error);
  }
};

// Función para verificar codigo de maquina
export const getUUIDByCodMachine = async (codMaquina: string): Promise<any> => {
  try {
    const response = await api.post<ApiResponse>('/getUUIDByCodMachine', { codMaquina });
    return handleApiResponse(response);
  } catch (error) {
    throw handleApiError(error);
  }
};
// Función para verificar codigo de maquina
export const getUUIDByCodMachineAll = async (): Promise<any> => {
  try {
    const response = await api.post<ApiResponse>('/getUUIDByCodMachineAll');
    return handleApiResponse(response);
  } catch (error) {
    throw handleApiError(error);
  }
};

// Función para registrar movimiento de cliente (ingreso/egreso de fichas)
export const registerClientMovement = async (movementData: any): Promise<any> => {
  try {
    const response = await api.post<ApiResponse>('/registerMovementClient', movementData);
    return handleApiResponse(response);
  } catch (error) {
    throw handleApiError(error);
  }
};

// Función para buscar cliente
export const searchClient = async (documentId: string): Promise<any> => {
  try {
    const response = await api.post<ApiResponse>('/findClient', { documentClientId: documentId });
    return handleApiResponse(response);
  } catch (error) {
    throw handleApiError(error);
  }
};

// Función para registrar nuevo cliente
export const registerClient = async (formData: FormData): Promise<ApiResponse> => {
  try {
    // Configuración específica para el envío de FormData
    const config = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    };

    const response = await api.post<ApiResponse>('/addClient', formData, config);
    
    // Log para depuración
    console.log('Datos enviados:', Object.fromEntries(formData));
    
    return handleApiResponse(response);
  } catch (error) {
    // Log para depuración
    if (error instanceof AxiosError) {
      console.error('Error en la petición:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
    }
    
    throw handleApiError(error as AxiosError);
  }
};

// Función para obtener lista de tarjetas del cliente
export const getClientCards = async (clientId: string): Promise<any> => {
  try {
     const response = await api.post<ApiResponse>('/listCardClient', { idCliente: clientId });
    return handleApiResponse(response);
  } catch (error) {
    throw handleApiError(error);
  }
};

// Función para operaciones con tarjetas de cliente (asociar, desasociar, etc.)
export const clientCardOperation = async (operationData: any): Promise<any> => {
  try {
    const response = await api.post<ApiResponse>('/clientCardOperation', operationData);
    return handleApiResponse(response);
  } catch (error) {
    throw handleApiError(error);
  }
};

// URLs para movimientos
const MOVEMENTS_URLS = {
  MOVEMENTS_CLIENT: '/movements_clients', // Para movimientos de cliente con cashregister_id: "0"
  MOVEMENTS_CAJA: '/movements',  // Para movimientos de caja con cashregister_id específico
  MOVEMENT_OPERATION: '/movementOperation'
} as const;

// Función para obtener movimientos de cliente
export const fetchClientMovements = async (userId: string, cajaId: string): Promise<ApiResponse> => {
  try {
    const response = await api.post<ApiResponse>(MOVEMENTS_URLS.MOVEMENTS_CLIENT, {
      idUser: userId,
      cashregister_id: cajaId
    });
    return handleApiResponse(response);
  } catch (error) {
    throw handleApiError(error);
  }
};

// Función para obtener movimientos de caja
export const fetchCajaMovements = async (userId: string, cajaId: string): Promise<ApiResponse> => {
  try {
    const response = await api.post<ApiResponse>(MOVEMENTS_URLS.MOVEMENTS_CAJA, {
      idUser: userId,
      cashregister_id: cajaId
    });
    return handleApiResponse(response);
  } catch (error) {
    throw handleApiError(error);
  }
};

// Función para anular movimiento
export const anularMovimiento = async (userId: string, movementId: string, cajaId: string): Promise<ApiResponse> => {
  try {
    const response = await api.post<ApiResponse>(MOVEMENTS_URLS.MOVEMENT_OPERATION, {
      idUser: userId,
      idMovimiento: movementId,
      idCaja: cajaId
    });
    return handleApiResponse(response);
  } catch (error) {
    throw handleApiError(error);
  }
};

// Función para anular movimiento de kiosco
export const anularMovimientoKiosco = async (
  userId: string, 
  movementId: string, 
  cajaId: string, 
  reason: string
): Promise<ApiResponse> => {
  try {
    const response = await api.post<ApiResponse>('/anularMovimientoKiosco', {
      userId: userId,
      movementId: movementId,
      cajaId: cajaId,
      reason: reason
    });
    return handleApiResponse(response);
  } catch (error) {
    throw handleApiError(error);
  }
};





// Añadir estos nuevos servicios
export const fetchCajaSummary = async (cajaId: string): Promise<ApiResponse> => {
  try {
    const response = await api.get<ApiResponse>(`/resumenCaja/${cajaId}`);
    return handleApiResponse(response);
  } catch (error) {
    throw handleApiError(error);
  }
};

export const generateCajaPDF = async (cajaId: string): Promise<Blob> => {
  try {
    const response = await api.get(`/generarPdfCaja/${cajaId}`, {
      responseType: 'blob',
      headers: {
        Accept: 'application/pdf',
      },
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

 

 
export type{
  ApiResponse
}